import { FC } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import Helmet from 'react-helmet';

import {
    ServerVarsContextConsumer,
    ServerVarsContextType,
} from '../../global/ServerVarsContext/ServerVarsContext';
import { SharedHeadLinkTags_metadata$data } from './__generated__/SharedHeadLinkTags_metadata.graphql';
import { useIntl } from 'dibs-react-intl';
import { handleLocaleUrl } from 'dibs-intl/exports/urls';
import { addTrailingSlash, addLeadingSlash } from '../../utils/uriUtils';

type Props = {
    metadata: SharedHeadLinkTags_metadata$data;
    localeOrigin?: ServerVarsContextType['localeOrigin'];
};

const SharedHeadLinkTagsComponent: FC<Props> = ({ metadata, localeOrigin }) => {
    const linkTagsArray = [];
    const intl = useIntl();
    if (!metadata.disableIndexing) {
        const { canonical, prev, next } = metadata;

        if (canonical) {
            linkTagsArray.push({
                rel: 'canonical',
                href: addTrailingSlash(
                    `${localeOrigin}${handleLocaleUrl(addLeadingSlash(canonical), intl.locale)}`
                ),
            });
        }

        if (prev) {
            linkTagsArray.push({
                rel: 'prev',
                href: addTrailingSlash(
                    `${localeOrigin}${handleLocaleUrl(addLeadingSlash(prev), intl.locale)}`
                ),
            });
        }

        if (next) {
            linkTagsArray.push({
                rel: 'next',
                href: addTrailingSlash(
                    `${localeOrigin}${handleLocaleUrl(addLeadingSlash(next), intl.locale)}`
                ),
            });
        }
    }

    return <Helmet link={linkTagsArray} />;
};

const SharedHeadLinkTagsServerVarsContextComponent: FC<Props> = props => {
    return (
        <ServerVarsContextConsumer>
            {({ localeOrigin }) => (
                <SharedHeadLinkTagsComponent {...props} localeOrigin={localeOrigin} />
            )}
        </ServerVarsContextConsumer>
    );
};

export const SharedHeadLinkTags = createFragmentContainer(
    SharedHeadLinkTagsServerVarsContextComponent,
    {
        metadata: graphql`
            fragment SharedHeadLinkTags_metadata on MetaData {
                canonical
                disableIndexing
                next
                prev
            }
        `,
    }
);
